import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";
import { visitorLotteryApi } from "../api/client";
import {
  useVisitorLotteryEventsQuery,
  useVisitorLotteryReportQuery,
} from "../api/queries";

function StatCard({ title, value }: { title: string; value: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-3xl font-semibold tabular-nums">
          {value}
        </CardTitle>
      </CardHeader>
    </Card>
  );
}

export function VisitorLotteryReportPage() {
  const nav = useNavigate();
  const { data: eventsData } = useVisitorLotteryEventsQuery();
  const eventId = eventsData?.items[0]?.id;
  const { data, isLoading } = useVisitorLotteryReportQuery(eventId);

  const handleExport = async () => {
    if (!eventId) return;
    const blob = await visitorLotteryApi.exportCsv({ eventId });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `visitor-lottery-report-${data?.event.slug ?? eventId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Visitor Lottery", href: "/app/visitor-lottery" },
            { label: "ລາຍງານ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">ລາຍງານສະຫຼຸບ</h1>
            <p className="text-muted-foreground text-sm">
              {data?.event.title ?? "Visitor Lottery"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/visitor-lottery" })}
            >
              ກັບໜ້າຕັ້ງຄ່າ
            </Button>
            {eventId ? (
              <Button variant="outline" onClick={() => void handleExport()}>
                <DownloadIcon className="mr-2 size-4" />
                ສົ່ງອອກ CSV
              </Button>
            ) : null}
          </div>
        </div>

        {isLoading || !data ? (
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        ) : (
          <div className="grid gap-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="ລົງທະບຽນທັງໝົດ"
                value={data.stats.totalRegistrations}
              />
              <StatCard title="ຖືກລາງວັນ" value={data.stats.prizeWins} />
              <StatCard title="ຮັບຂອງແລ້ວ" value={data.stats.redeemed} />
              <StatCard
                title="ລາງວັນເຫຼືອ"
                value={data.stats.prizeRemainingTotal}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>ສະຫຼຸບຕາມລາງວັນ</CardTitle>
              </CardHeader>
              <div className="overflow-x-auto px-4 pb-4">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2">ລາງວັນ</th>
                      <th className="p-2">ໂໝດ</th>
                      <th className="p-2">ຈຳນວນ</th>
                      <th className="p-2">ເຫຼືອ</th>
                      <th className="p-2">ຜູ້ໄດ້</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.prizes.map((p) => (
                      <tr key={p.id} className="border-t">
                        <td className="p-2 font-medium">{p.name}</td>
                        <td className="p-2">
                          {p.isConsolation ? (
                            <Badge variant="secondary">Consolation</Badge>
                          ) : (
                            <Badge variant="outline">
                              {p.drawMode === "deferred"
                                ? "Deferred"
                                : "Instant"}
                            </Badge>
                          )}
                        </td>
                        <td className="p-2">{p.quantity}</td>
                        <td className="p-2">{p.remaining}</td>
                        <td className="p-2">{p.winCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>ຜູ້ໄດ້ລາງວັນ (ມີໂຄດ)</CardTitle>
                <CardDescription>
                  {data.winners.length} ລາຍການ (ສູງສຸດ 200)
                </CardDescription>
              </CardHeader>
              <div className="overflow-x-auto px-4 pb-4">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2">ຊື່</th>
                      <th className="p-2">ເບີ</th>
                      <th className="p-2">ລາງວັນ</th>
                      <th className="p-2">ໂຄດ</th>
                      <th className="p-2">ຮັບຂອງ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.winners.map((w) => (
                      <tr key={w.id} className="border-t">
                        <td className="p-2">{w.fullName}</td>
                        <td className="p-2 font-mono text-xs">
                          {w.phoneNumber}
                        </td>
                        <td className="p-2">{w.prizeNameSnapshot}</td>
                        <td className="p-2 font-mono text-xs">
                          {w.redeemCode}
                        </td>
                        <td className="p-2">
                          {w.redeemedAt ? (
                            <Badge>ຮັບແລ້ວ</Badge>
                          ) : (
                            <Badge variant="secondary">ລໍຖ້າ</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </Main>
    </>
  );
}
